import React, { FC, memo, useCallback } from 'react';
import { useParams } from 'react-router';
import { View, ViewType } from 'api/views/types';
import { confirm } from 'components/atoms/modal';
import { Filter } from 'components/molecules/filters/types';
import { ManageProjectViewDrawer } from 'components/molecules/views/manage-project-view-drawer';
import { useProjectViewActions } from 'components/molecules/views/project-view-actions';
import { AllViews } from 'components/molecules/views/saved-views/views';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useTranslate } from 'hooks/use-translation';
import { useUpdateProjectTaskViewMutation } from 'queries/task-view';
import { ColumnOrder } from '../grid-custom-column';
import { Sider } from './group-filter-menu';
import { useFilterMenu } from './use-filter-menu';
import { GroupBy } from './use-group-by-hash';

interface Props {
  filter: Filter;
  isFieldsLoading: boolean;
  fields: FilterableField[];
  lookup: Record<string, FilterableField>;
  selectFilter: (filter: Filter) => void;
  clearFilters: () => void;
  setKanbanBoardGroupChangeLoading?: (value: boolean) => void;
  groupBy?: GroupBy;
  setGroupBy?: (value: GroupBy, replace?: boolean) => void;
  columnOrder?: ColumnOrder[];
  handleColumnOrderChange?: (changedOrder: ColumnOrder[]) => void;
  defaultColumnOrder?: ColumnOrder[];
}

const _FilterMenu: FC<Props> = ({
  filter,
  lookup,
  fields,
  isFieldsLoading,
  selectFilter,
  clearFilters,
  setKanbanBoardGroupChangeLoading,
  groupBy,
  setGroupBy,
  columnOrder,
  handleColumnOrderChange,
  defaultColumnOrder = [],
}) => {
  const translate = useTranslate();
  const { projectId } = useParams<{ projectId: string }>();
  const {
    taskListViews,
    viewDefinitionTransformer,
    currentViewId,
    onViewSelect,
    defaultViewsList,
    defaultViewId,
    manageViewModalVisible,
    setManageViewModalVisible,
    setCurrentEditingView,
    currentEditingView,
    selectedView,
    onViewEdit,
    isViewsLoading,
    onDeleteSuccess,
    onDeleteFailure,
  } = useFilterMenu({
    fields,
    lookup,
    isFieldsLoading,
    setKanbanBoardGroupChangeLoading,
    selectFilter,
    clearFilters,
    setGroupBy,
    projectId,
    handleColumnOrderChange,
    defaultColumnOrder,
  });

  const { onUpdate: onProjectTaskViewUpdate } =
    useUpdateProjectTaskViewMutation(projectId);

  const { onProjectViewUpdate, onProjectViewDelete } = useProjectViewActions({
    views: taskListViews,
    setViews: onProjectTaskViewUpdate,
    viewType: ViewType.ProjectTaskList,
    transformer: viewDefinitionTransformer,
  });

  const onDelete = useCallback(
    (view: View<any>) => {
      confirm({
        okText: translate('delete'),
        okType: 'danger',
        cancelText: translate('cancel'),
        onCancel: () => {},
        title: translate('views.delete_confirmation.title'),
        content: (
          <>
            {translate('views.delete_confirmation.content', {
              viewName: view.viewName,
              strong: (chunks) => <strong>{chunks}</strong>,
            })}
          </>
        ),
        onOk: () => {
          if (view.viewId === currentViewId) {
            onViewSelect(defaultViewsList.byIds[defaultViewId][1]);
          }

          onProjectViewDelete(view)
            .then(() => onDeleteSuccess(view))
            .catch(() => onDeleteFailure(view));
        },
      });
    },
    [
      translate,
      currentViewId,
      onViewSelect,
      defaultViewsList.byIds,
      defaultViewId,
      onProjectViewDelete,
      onDeleteSuccess,
      onDeleteFailure,
    ],
  );

  return (
    <Sider>
      <ManageProjectViewDrawer
        visible={manageViewModalVisible}
        onClose={() => {
          setManageViewModalVisible(false);
          setCurrentEditingView(undefined);
        }}
        view={currentEditingView}
        onViewUpdate={onProjectViewUpdate}
        currentViewDefinition={{
          filter,
          groupBy,
          columnOrder,
        }}
      />

      <AllViews
        views={taskListViews}
        defaultViewsList={defaultViewsList}
        defaultViewId={defaultViewId}
        onViewSelect={onViewSelect}
        selectedView={selectedView}
        onViewEdit={onViewEdit}
        onViewDelete={onDelete}
        loading={isViewsLoading}
      />
    </Sider>
  );
};

export const FilterMenuProject = memo(_FilterMenu);
